// SCR-STAFF-806 (AP-CMP-01؛ US-CMP-070): گزارش سهم و درآمد به تفکیک ارائه‌دهنده، خدمت، شعبه یا ماه شمسی — تعداد، مبنا، سهم ارائه‌دهنده، سهم مرکز،
// کمیسیون، اصلاحیه‌ها، Unassigned و NeedsReview؛ پیش‌فرض ماه شمسی جاری. زبانه سودآوری خدمت (AP-CMP-02؛ US-CMP-071؛ C-AGENT-1387): درآمد، تخفیف،
// سهم ارائه‌دهندگان، مواد مصرفی، استهلاک دستگاه، کسور بیمه و سود.
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import {
  unwrap,
  type CmpProfitabilityRow,
  type CmpShareReportGroup,
  type CmpShareReportRow,
} from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  faDigits,
  jalaliToBusinessDate,
  JalaliDatePickerRHF,
  ListPage,
  Money,
  toJalali,
  type DataTableColumn,
} from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { compensationKeys, REPORT_GROUPS } from './common';

export const SCR_STAFF_806 = 'SCR-STAFF-806';

/** نخستین و آخرین روز ماه شمسی جاری (دوره پیش‌فرض — BR-CMP-040). */
export function currentJalaliMonth(): { from: string; to: string } {
  const [year = 1405, month = 1] = toJalali(new Date(), { pattern: 'yyyy/MM/dd', persianDigits: false })
    .split('/')
    .map(Number);
  const from = jalaliToBusinessDate(year, month, 1);
  const next = month === 12 ? jalaliToBusinessDate(year + 1, 1, 1) : jalaliToBusinessDate(year, month + 1, 1);
  const end = new Date(`${next}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() - 1);
  return { from, to: end.toISOString().slice(0, 10) };
}

export function ShareReportPage() {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const [groupBy, setGroupBy] = useState<CmpShareReportGroup>('Provider');
  const [tab, setTab] = useState<'shares' | 'profitability'>('shares');
  const { control } = useForm<{ from: string; to: string }>({ defaultValues: currentJalaliMonth() });
  const from = useWatch({ control, name: 'from' });
  const to = useWatch({ control, name: 'to' });

  const report = useQuery({
    queryKey: [...compensationKeys.report(session?.tenantId), from, to, groupBy],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/compensation/reports/shares', { params: { query: { from, to, groupBy } } }),
      ),
    enabled: Boolean(from && to) && tab === 'shares',
  });

  const label = (row: CmpShareReportRow) => {
    if (row.key === 'total') {
      return t('cmp:report.total');
    }
    if (row.key === 'none') {
      return t('cmp:report.unattributed');
    }
    return row.label ?? row.key;
  };

  const columns: DataTableColumn<CmpShareReportRow>[] = [
    { id: 'key', headerKey: 'cmp:report.columns.key', cell: (row) => label(row) },
    {
      id: 'lines',
      headerKey: 'cmp:report.columns.lines',
      cell: (row) => faDigits(Number(row.lines)),
      align: 'right',
    },
    {
      id: 'base',
      headerKey: 'cmp:report.columns.base',
      cell: (row) => <Money amountMinor={Number(row.base.amount)} />,
      align: 'right',
    },
    {
      id: 'providerShare',
      headerKey: 'cmp:report.columns.provider_share',
      cell: (row) => <Money amountMinor={Number(row.providerShare.amount)} />,
      align: 'right',
    },
    {
      id: 'centerShare',
      headerKey: 'cmp:report.columns.center_share',
      cell: (row) => <Money amountMinor={Number(row.centerShare.amount)} />,
      align: 'right',
    },
    {
      id: 'commissions',
      headerKey: 'cmp:report.columns.commissions',
      cell: (row) => <Money amountMinor={Number(row.commissions.amount)} />,
      align: 'right',
    },
    {
      id: 'corrections',
      headerKey: 'cmp:report.columns.corrections',
      cell: (row) => <Money amountMinor={Number(row.corrections.amount)} />,
      align: 'right',
    },
    {
      id: 'unassigned',
      headerKey: 'cmp:report.columns.unassigned',
      cell: (row) =>
        Number(row.unassignedLines) > 0 ? (
          <span data-testid={`report-unassigned-${row.key}`}>{faDigits(Number(row.unassignedLines))}</span>
        ) : (
          '—'
        ),
      align: 'right',
    },
    {
      id: 'needsReview',
      headerKey: 'cmp:report.columns.needs_review',
      cell: (row) =>
        Number(row.needsReviewLines) > 0 ? <Money amountMinor={Number(row.needsReviewAmount.amount)} /> : '—',
      align: 'right',
    },
  ];

  const rows = report.data
    ? [...report.data.rows, ...(report.data.rows.length > 0 ? [report.data.total] : [])]
    : undefined;

  return (
    <ListPage
      screenId={SCR_STAFF_806}
      titleKey="cmp:report.title"
      subtitleKey="cmp:report.subtitle"
      filters={
        <Stack spacing={1}>
          <Tabs value={tab} onChange={(_, value: 'shares' | 'profitability') => setTab(value)}>
            <Tab value="shares" label={t('cmp:report.tab_shares')} data-testid="report-tab-shares" />
            <Tab
              value="profitability"
              label={t('cmp:report.tab_profitability')}
              data-testid="report-tab-profitability"
            />
          </Tabs>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <JalaliDatePickerRHF control={control} name="from" labelKey="cmp:report.from" />
            <JalaliDatePickerRHF control={control} name="to" labelKey="cmp:report.to" />
            {tab === 'shares' && (
              <TextField
                select
                size="small"
                label={t('cmp:report.group_by')}
                value={groupBy}
                onChange={(event) => setGroupBy(event.target.value as CmpShareReportGroup)}
                sx={{ minWidth: 180 }}
                slotProps={{ htmlInput: { 'data-testid': 'report-group-by' } }}
              >
                {REPORT_GROUPS.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`cmp:report.group.${value}`)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
        </Stack>
      }
    >
      {tab === 'profitability' ? (
        <ProfitabilityTable from={from} to={to} />
      ) : (
        <DataTable
          captionKey="cmp:report.caption"
          columns={columns}
          rows={rows}
          getRowId={(row) => row.key}
          isLoading={report.isPending}
          error={report.error}
          onRetry={() => void report.refetch()}
          emptyTitleKey="cmp:report.empty"
        />
      )}
    </ListPage>
  );
}

/** سودآوری خدمت (US-CMP-071): سود = درآمد خالص − سهم‌ها − مواد مصرفی − دستگاه − کسور بیمه. */
function ProfitabilityTable({ from, to }: { from: string; to: string }) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const report = useQuery({
    queryKey: [...compensationKeys.profitability(session?.tenantId), from, to],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/compensation/reports/profitability', { params: { query: { from, to } } }),
      ),
    enabled: Boolean(from && to),
  });
  const money = (value: { amount: number | string }) => <Money amountMinor={Number(value.amount)} />;
  const columns: DataTableColumn<CmpProfitabilityRow>[] = [
    {
      id: 'service',
      headerKey: 'cmp:report.profit.service',
      cell: (row) => row.serviceName ?? t('cmp:report.total'),
    },
    {
      id: 'lines',
      headerKey: 'cmp:report.columns.lines',
      cell: (row) => faDigits(Number(row.lines)),
      align: 'right',
    },
    {
      id: 'revenue',
      headerKey: 'cmp:report.profit.revenue',
      cell: (row) => money(row.revenue),
      align: 'right',
    },
    {
      id: 'discounts',
      headerKey: 'cmp:report.profit.discounts',
      cell: (row) => money(row.discounts),
      align: 'right',
    },
    { id: 'shares', headerKey: 'cmp:report.profit.shares', cell: (row) => money(row.shares), align: 'right' },
    {
      id: 'consumables',
      headerKey: 'cmp:report.profit.consumables',
      cell: (row) => money(row.consumables),
      align: 'right',
    },
    { id: 'device', headerKey: 'cmp:report.profit.device', cell: (row) => money(row.device), align: 'right' },
    {
      id: 'insurance',
      headerKey: 'cmp:report.profit.insurance',
      cell: (row) => money(row.insuranceDeductions),
      align: 'right',
    },
    {
      id: 'profit',
      headerKey: 'cmp:report.profit.profit',
      cell: (row) => <span data-testid={`profit-${row.serviceId ?? 'total'}`}>{money(row.profit)}</span>,
      align: 'right',
    },
  ];
  const rows = report.data
    ? [...report.data.rows, ...(report.data.rows.length > 0 ? [report.data.total] : [])]
    : undefined;
  return (
    <DataTable
      captionKey="cmp:report.profit.caption"
      columns={columns}
      rows={rows}
      getRowId={(row) => row.serviceId ?? 'total'}
      isLoading={report.isPending}
      error={report.error}
      onRetry={() => void report.refetch()}
      emptyTitleKey="cmp:report.empty"
    />
  );
}
